const normalizeRuleString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim().toLowerCase();
};

const resolveNotaFieldForRule = (nota, field, contexto = {}, helpers) => {
  if (!nota) return null;

  switch (field) {
    case 'tipo_operacao':
      return nota.tipo_operacao ? String(nota.tipo_operacao).toLowerCase() : '';
    case 'emitente_cnpj':
      return helpers.sanitizeCnpj(nota.emitente?.cnpj);
    case 'destinatario_cnpj':
      return helpers.sanitizeCnpj(nota.destinatario?.cnpj);
    case 'valor_total':
      return helpers.parseCurrencyString(nota.valor_total);
    case 'cfop':
      return nota.cfop ? String(nota.cfop).trim() : '';
    case 'natureza_operacao':
      return nota.natureza_operacao ? String(nota.natureza_operacao).trim() : '';
    case 'tipo_documento':
      if (contexto?.tipoDocumento) return String(contexto.tipoDocumento).toLowerCase();
      return nota.origem === 'xml_cte' ? 'cte' : 'nfe';
    default:
      return nota[field];
  }
};

const evaluateNotaImportRuleCondition = (nota, condition, contexto = {}, helpers) => {
  if (!condition) return true;

  const operator = condition.operator || 'equals';
  const rawValue = resolveNotaFieldForRule(nota, condition.field, contexto, helpers);
  const expected = condition.value;

  if (operator === 'greater_than' || operator === 'less_than') {
    const left = Number(rawValue) || 0;
    const right = Number(expected) || 0;
    if (operator === 'greater_than') {
      return left > right;
    }
    return left < right;
  }

  const left = normalizeRuleString(rawValue);
  const right = normalizeRuleString(expected);

  switch (operator) {
    case 'equals':
      return left === right;
    case 'not_equals':
      return left !== right;
    case 'contains':
      return left.includes(right);
    case 'starts_with':
      return left.startsWith(right);
    case 'ends_with':
      return left.endsWith(right);
    default:
      return left === right;
  }
};

const parseDateFromIso = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDateToIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDaysToIsoDate = (dateString, days) => {
  const baseDate = parseDateFromIso(dateString);
  if (!baseDate) return null;
  const adjusted = new Date(baseDate.getTime());
  adjusted.setUTCDate(adjusted.getUTCDate() + days);
  return formatDateToIso(adjusted);
};

const resolveReferenceDueDate = (nota) => {
  if (!nota) return null;
  if (nota.data_vencimento) return nota.data_vencimento;

  if (Array.isArray(nota.parcelas)) {
    const parcelaComData = nota.parcelas.find((parcela) => parcela?.data_vencimento);
    if (parcelaComData?.data_vencimento) {
      return parcelaComData.data_vencimento;
    }
  }

  if (nota.data_emissao) return nota.data_emissao;

  return null;
};

const resolveDueDateBaseDate = (nota, params) => {
  if (!nota) return null;

  const base = typeof params?.base === 'string' ? params.base.toLowerCase() : 'auto';

  if (base === 'current_date' || base === 'data_atual') {
    return formatDateToIso(new Date());
  }

  if (base === 'issue_date' || base === 'data_emissao') {
    return nota.data_emissao || null;
  }

  if (base === 'due_date' || base === 'data_vencimento') {
    const due = nota.data_vencimento;
    if (due) return due;
    if (Array.isArray(nota.parcelas)) {
      const parcela = nota.parcelas.find((item) => item?.data_vencimento);
      if (parcela?.data_vencimento) return parcela.data_vencimento;
    }
    return nota.data_emissao || null;
  }

  if (base && base !== 'auto') {
    return resolveReferenceDueDate(nota);
  }

  return resolveReferenceDueDate(nota) || formatDateToIso(new Date());
};

const applySetDueDateOffsetAction = (nota, params, changedFields, helpers) => {
  const days = Number(params?.days ?? 0);
  if (!Number.isFinite(days)) {
    return [];
  }

  const referenceDate = resolveDueDateBaseDate(nota, params) || formatDateToIso(new Date());
  const novaData = addDaysToIsoDate(referenceDate, days);
  if (!novaData) {
    return [];
  }

  const mensagens = [];
  if (nota.data_vencimento !== novaData) {
    nota.data_vencimento = novaData;
    changedFields.add('data_vencimento');
    const prefix = days === 0 ? '' : ` (${days > 0 ? '+' : ''}${days} dia${Math.abs(days) === 1 ? '' : 's'})`;
    mensagens.push(`Data de vencimento ajustada para ${novaData}${prefix}`);
  }

  let parcelasAjustadas = 0;
  if (Array.isArray(nota.parcelas) && nota.parcelas.length > 0) {
    nota.parcelas = nota.parcelas.map((parcela) => {
      const base =
        params?.base && params.base !== 'auto'
          ? referenceDate
          : parcela?.data_vencimento || referenceDate;
      const dataParcela = addDaysToIsoDate(base, days) || novaData;
      if (parcela?.data_vencimento !== dataParcela) {
        parcelasAjustadas += 1;
        return { ...parcela, data_vencimento: dataParcela };
      }
      return parcela;
    });

    if (parcelasAjustadas > 0) {
      changedFields.add('parcelas');
      mensagens.push(
        `${parcelasAjustadas} parcela${parcelasAjustadas === 1 ? '' : 's'} ajustada${parcelasAjustadas === 1 ? '' : 's'} automaticamente.`
      );
    }
  } else {
    const valorTotal = helpers.parseCurrencyString(nota.valor_total);
    if (valorTotal > 0) {
      nota.parcelas = [
        {
          numero: '1',
          data_vencimento: novaData,
          valor: valorTotal
        }
      ];
      changedFields.add('parcelas');
      mensagens.push('Parcela padrão criada com base no valor total da nota.');
    }
  }

  return mensagens;
};

const applyNotaImportRuleAction = (nota, action, contexto, changedFields, helpers) => {
  if (!action || !action.type) {
    return [];
  }

  if (action.type === 'set_due_date_offset') {
    return applySetDueDateOffsetAction(nota, action.params, changedFields, helpers);
  }

  if (action.type === 'set_financial_type') {
    const financialType = action.params?.financialType || action.params?.tipo || 'automatico';
    const mensagens = [];

    if (financialType === 'automatico') {
      const tipoInferido = helpers.inferTipoOperacao(
        nota.emitente?.cnpj,
        nota.destinatario?.cnpj,
        contexto?.empresa?.cnpj || contexto?.empresaCnpj
      );
      if (tipoInferido && nota.tipo_operacao !== tipoInferido) {
        nota.tipo_operacao = tipoInferido;
        changedFields.add('tipo_operacao');
        mensagens.push(`Tipo financeiro ajustado automaticamente para "${tipoInferido}".`);
      }
    } else if (nota.tipo_operacao !== financialType) {
      nota.tipo_operacao = financialType;
      changedFields.add('tipo_operacao');
      mensagens.push(`Tipo financeiro definido como "${financialType}".`);
    }

    return mensagens;
  }

  if (action.type === 'set_gera_financeiro') {
    const enabled = action.params?.enabled !== undefined ? Boolean(action.params.enabled) : true;
    const atual = helpers.parseBooleanFlag(nota.gera_financeiro, true);
    if (atual !== enabled) {
      nota.gera_financeiro = enabled;
      changedFields.add('gera_financeiro');
      return [enabled ? 'Geração financeira ativada automaticamente.' : 'Geração financeira desativada automaticamente.'];
    }
    return [];
  }

  return [];
};

const defaultHelpers = {
  parseCurrencyString: (value) => {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return 0;
    const raw = String(value).trim();
    if (!raw) return 0;
    const normalized = raw.replace(/[^0-9,-.]/g, '').replace(/,(?=\d{3}(\D|$))/g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  },
  sanitizeCnpj: (value) => (value ? String(value).replace(/\D/g, '') : ''),
  parseBooleanFlag: (value, defaultValue = true) => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    const normalized = String(value).trim().toLowerCase();
    if (['1', 'true', 't', 'yes', 'y', 'sim'].includes(normalized)) return true;
    if (['0', 'false', 'f', 'no', 'n', 'nao', 'não'].includes(normalized)) return false;
    return defaultValue;
  },
  inferTipoOperacao: () => null
};

export const applyNotaImportRules = (nota, regras, contexto = {}, helpers = {}) => {
  const mergedHelpers = { ...defaultHelpers, ...helpers };

  if (!nota || !Array.isArray(regras) || regras.length === 0) {
    return {
      nota: nota ? JSON.parse(JSON.stringify(nota)) : nota,
      appliedRules: [],
      adjustments: [],
      changedFields: []
    };
  }

  const notaAjustada = JSON.parse(JSON.stringify(nota));
  const regrasAtivas = regras
    .filter((regra) => regra && regra.is_active !== false)
    .sort((a, b) => {
      const prioridadeA = Number.isFinite(Number(a?.prioridade)) ? Number(a.prioridade) : 0;
      const prioridadeB = Number.isFinite(Number(b?.prioridade)) ? Number(b.prioridade) : 0;
      if (prioridadeA === prioridadeB) {
        const idA = typeof a?.id === 'number' ? a.id : 0;
        const idB = typeof b?.id === 'number' ? b.id : 0;
        return idA - idB;
      }
      return prioridadeA - prioridadeB;
    });

  const aplicadas = [];
  const ajusteDetalhes = [];
  const camposAlterados = new Set();

  for (const regra of regrasAtivas) {
    const condicoes = Array.isArray(regra.conditions) ? regra.conditions : [];
    const todasCondicoesAtendidas = condicoes.every((condition) =>
      evaluateNotaImportRuleCondition(notaAjustada, condition, contexto, mergedHelpers)
    );

    if (!todasCondicoesAtendidas) {
      continue;
    }

    const acaoMensagens = [];
    const acoes = Array.isArray(regra.actions) ? regra.actions : [];
    for (const action of acoes) {
      const mensagens = applyNotaImportRuleAction(
        notaAjustada,
        action,
        contexto,
        camposAlterados,
        mergedHelpers
      );
      if (mensagens.length > 0) {
        acaoMensagens.push(...mensagens);
        mensagens.forEach((mensagem) => {
          ajusteDetalhes.push({
            ruleId: regra.id ?? null,
            ruleName: regra.nome || 'Regra de importação',
            message: mensagem,
            actionType: action.type
          });
        });
      }
    }

    if (acaoMensagens.length > 0) {
      aplicadas.push({
        id: regra.id ?? null,
        nome: regra.nome || 'Regra de importação',
        prioridade: regra.prioridade ?? 0,
        adjustments: acaoMensagens
      });
    }
  }

  return {
    nota: notaAjustada,
    appliedRules: aplicadas,
    adjustments: ajusteDetalhes,
    changedFields: Array.from(camposAlterados)
  };
};
